import React, { useCallback, useEffect, useRef, useState } from "react";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { useSelector } from "react-redux";
import api from "utils/api";
import { bookingPriceLabel } from "utils/bookingPayment";
import { adminBrandParams } from "utils/brand";
import { useTeamAdminBrand, useTeamAdminOrt } from "./TeamAdminBrandContext";
import { useTeamLocale } from "./TeamLocaleContext";
import {
  formatLong,
  formatShortDateTime,
  formatEuro,
  statusLabel,
  cancelledByLabel,
  paymentLabel,
  sourceLabel,
  translateServiceLabel,
  formatBookingCustomerNotes,
  internalNotesValue,
} from "./formatters";
import TeamDeleteBookingDialog from "./TeamDeleteBookingDialog";
import { useTeamAdminLiveOptional } from "./TeamAdminLiveContext";
import ConfirmDialog from "components/ConfirmDialog";

const STATUS_OPTIONS = [
  "pending-employee-confirmation",
  "confirmed",
  "completed",
  "no-show",
];

const isReadOnlyStatus = (status) =>
  status === "pending-email-verification" ||
  status === "reserved-awaiting-payment" ||
  status === "awaiting-immediate-payment" ||
  status === "expired" ||
  status === "pending-payment";

const isOnlinePaidBooking = (booking) =>
  booking?.paymentMethod === "stripe" && booking?.paymentStatus === "paid";

const SORT_FIELD_APPOINTMENT = "appointment";
const SORT_FIELD_CREATED = "created";

const BookingsTab = ({ manualOnly = false, reloadSignal = 0, leadKey = "bookings.lead" }) => {
  const { t, dateLocale, locale } = useTeamLocale();
  const adminBrand = useTeamAdminBrand();
  const adminOrt = useTeamAdminOrt();
  const [bookings, setBookings] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [includePastAppointments, setIncludePastAppointments] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [showExpired, setShowExpired] = useState(false);
  const [appointmentSort, setAppointmentSort] = useState("asc");
  const [createdSort, setCreatedSort] = useState("desc");
  const [sortField, setSortField] = useState(SORT_FIELD_APPOINTMENT);
  const [savingId, setSavingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteVariant, setDeleteVariant] = useState("cancel");
  const [noShowConfirm, setNoShowConfirm] = useState(null);
  const isSuperAdmin = useSelector((s) => Boolean(s.user?.isSuperAdmin));
  const pageRef = useRef(1);
  const live = useTeamAdminLiveOptional();

  const load = useCallback(
    async (page = 1, { silent = false } = {}) => {
      pageRef.current = page;
      if (!silent) setLoading(true);
      setError(null);
      try {
        const { data } = await api.get("/admin/bookings", {
          params: {
            ...adminBrandParams({ brand: adminBrand, ort: adminOrt }),
            page,
            limit: 20,
            status: statusFilter || undefined,
            upcoming: includePastAppointments ? undefined : "true",
            includeCancelled: showCancelled ? "true" : undefined,
            includeExpired: showExpired ? "true" : undefined,
            sortStart: sortField === SORT_FIELD_APPOINTMENT ? appointmentSort : undefined,
            sortCreated:
              manualOnly && sortField === SORT_FIELD_CREATED ? createdSort : undefined,
            manual: manualOnly ? "true" : undefined,
          },
        });
        setBookings(data.bookings || []);
        setPagination(data.pagination || { page: 1, pages: 1, total: 0 });
      } catch (err) {
        if (!silent) {
          setError(err?.response?.data?.message || t("bookings.loadError"));
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [
      statusFilter,
      includePastAppointments,
      showCancelled,
      showExpired,
      appointmentSort,
      createdSort,
      sortField,
      adminBrand,
      adminOrt,
      manualOnly,
      t,
    ]
  );

  useEffect(() => {
    load(1, { silent: false });
  }, [load]);

  useEffect(() => {
    if (!live?.refreshTick) return;
    load(pageRef.current, { silent: true });
  }, [live?.refreshTick, load]);

  useEffect(() => {
    if (!reloadSignal) return;
    load(1, { silent: true });
  }, [reloadSignal, load]);

  const handleStatusChange = async (id, status) => {
    setSavingId(id);
    try {
      const { data } = await api.patch(`/admin/bookings/${id}`, { status }, {
        params: adminBrandParams({ brand: adminBrand }),
      });
      setBookings((prev) =>
        prev.map((b) => (b._id === id ? { ...b, ...data.booking } : b))
      );
    } catch (err) {
      setError(err?.response?.data?.message || t("bookings.saveError"));
    } finally {
      setSavingId(null);
    }
  };

  const handleStatusSelectChange = (booking, newStatus) => {
    if (newStatus === "no-show" && booking.status !== "no-show") {
      setNoShowConfirm(booking);
      return;
    }
    handleStatusChange(booking._id, newStatus);
  };

  const confirmNoShow = async () => {
    if (!noShowConfirm) return;
    const id = noShowConfirm._id;
    await handleStatusChange(id, "no-show");
    setNoShowConfirm(null);
  };

  const handleNotesBlur = async (id, notes) => {
    setSavingId(id);
    try {
      const { data } = await api.patch(`/admin/bookings/${id}`, { notes }, {
        params: adminBrandParams({ brand: adminBrand }),
      });
      setBookings((prev) =>
        prev.map((b) => (b._id === id ? { ...b, ...data.booking } : b))
      );
    } catch (err) {
      setError(err?.response?.data?.message || t("bookings.notesError"));
    } finally {
      setSavingId(null);
    }
  };

  const openDeleteDialog = (booking, variant) => {
    setDeleteVariant(variant);
    setDeleteTarget(booking);
  };

  const confirmDelete = async (reason) => {
    if (!deleteTarget) return;
    setSavingId(deleteTarget._id);
    setError(null);
    try {
      if (deleteVariant === "permanent") {
        await api.delete(`/admin/bookings/${deleteTarget._id}/permanent`, {
          params: adminBrandParams({ brand: adminBrand }),
          data: { reason: reason || undefined },
        });
      } else {
        await api.delete(`/admin/bookings/${deleteTarget._id}`, {
          params: adminBrandParams({ brand: adminBrand }),
          data: { reason },
        });
      }
      setBookings((prev) => prev.filter((b) => b._id !== deleteTarget._id));
      setPagination((prev) => ({
        ...prev,
        total: Math.max(0, prev.total - 1),
      }));
      setDeleteTarget(null);
    } catch (err) {
      setError(err?.response?.data?.message || t("bookings.deleteError"));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="team-bookings">
      <p className="team-lead">{t(leadKey)}</p>

      <div className="team-filters">
        <label>
          {t("bookings.statusFilter")}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">{t("common.all")}</option>
            {STATUS_OPTIONS.map((val) => (
              <option key={val} value={val}>
                {statusLabel(t, val)}
              </option>
            ))}
            <option value="reserved-awaiting-payment">
              {statusLabel(t, "reserved-awaiting-payment")}
            </option>
            <option value="awaiting-immediate-payment">
              {statusLabel(t, "awaiting-immediate-payment")}
            </option>
            {showExpired && (
              <option value="expired">{statusLabel(t, "expired")}</option>
            )}
            {showCancelled && (
              <option value="cancelled">{statusLabel(t, "cancelled")}</option>
            )}
          </select>
        </label>
        <label className="team-checkbox">
          <input
            type="checkbox"
            checked={includePastAppointments}
            onChange={(e) => setIncludePastAppointments(e.target.checked)}
          />
          {t("bookings.includePast")}
        </label>
        <label className="team-checkbox">
          <input
            type="checkbox"
            checked={showCancelled}
            onChange={(e) => {
              const checked = e.target.checked;
              setShowCancelled(checked);
              if (!checked && statusFilter === "cancelled") setStatusFilter("");
            }}
          />
          {t("bookings.showCancelled")}
        </label>
        <label className="team-checkbox">
          <input
            type="checkbox"
            checked={showExpired}
            onChange={(e) => {
              const checked = e.target.checked;
              setShowExpired(checked);
              if (!checked && statusFilter === "expired") setStatusFilter("");
            }}
          />
          {t("bookings.showExpired")}
        </label>
      </div>

      {error && <div className="auth-alert is-error">{error}</div>}

      {loading ? (
        <div className="team-empty">{t("bookings.loading")}</div>
      ) : bookings.length === 0 ? (
        <div className="team-empty">{t("bookings.empty")}</div>
      ) : (
        <div className="team-table-wrap">
          <table className="team-table">
            <thead>
              <tr>
                <th
                  className={`team-table-th-sortable${
                    manualOnly ? " team-booking-appointment-th" : ""
                  }`}
                >
                  <span>{t("bookings.col.appointment")}</span>
                  <button
                    type="button"
                    className="team-table-sort-btn"
                    onClick={() => {
                      const wasActive = sortField === SORT_FIELD_APPOINTMENT;
                      setSortField(SORT_FIELD_APPOINTMENT);
                      setAppointmentSort((current) =>
                        wasActive ? (current === "asc" ? "desc" : "asc") : current
                      );
                    }}
                    aria-label={
                      appointmentSort === "asc"
                        ? t("bookings.sort.appointmentAsc")
                        : t("bookings.sort.appointmentDesc")
                    }
                    title={
                      appointmentSort === "asc"
                        ? t("bookings.sort.appointmentAsc")
                        : t("bookings.sort.appointmentDesc")
                    }
                  >
                    {appointmentSort === "asc" ? (
                      <ArrowUpwardIcon fontSize="inherit" aria-hidden="true" />
                    ) : (
                      <ArrowDownwardIcon fontSize="inherit" aria-hidden="true" />
                    )}
                  </button>
                </th>
                {manualOnly && (
                  <th className="team-table-th-sortable">
                    <span>{t("bookings.col.created")}</span>
                    <button
                      type="button"
                      className="team-table-sort-btn"
                      onClick={() => {
                        const wasActive = sortField === SORT_FIELD_CREATED;
                        setSortField(SORT_FIELD_CREATED);
                        setCreatedSort((current) =>
                          wasActive ? (current === "asc" ? "desc" : "asc") : current
                        );
                      }}
                      aria-label={
                        createdSort === "asc"
                          ? t("bookings.sort.createdAsc")
                          : t("bookings.sort.createdDesc")
                      }
                      title={
                        createdSort === "asc"
                          ? t("bookings.sort.createdAsc")
                          : t("bookings.sort.createdDesc")
                      }
                    >
                      {createdSort === "asc" ? (
                        <ArrowUpwardIcon fontSize="inherit" aria-hidden="true" />
                      ) : (
                        <ArrowDownwardIcon fontSize="inherit" aria-hidden="true" />
                      )}
                    </button>
                  </th>
                )}
                <th>{t("bookings.col.service")}</th>
                <th>{t("bookings.col.customer")}</th>
                <th>{t("bookings.col.price")}</th>
                <th>{t("bookings.col.payment")}</th>
                <th>{t("bookings.col.status")}</th>
                <th>{t("bookings.col.customerNotes")}</th>
                <th>{t("bookings.col.notes")}</th>
                <th>{t("bookings.col.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => {
                const customerNotes = formatBookingCustomerNotes(b);
                const displayedStatus = isOnlinePaidBooking(b) ? "completed" : b.status;
                return (
                <tr key={b._id} className={b.status === "cancelled" || b.status === "expired" ? "is-muted" : ""}>
                  <td className={manualOnly ? "team-booking-appointment" : undefined}>
                    {formatLong(b.start, dateLocale)}
                  </td>
                  {manualOnly && (
                    <td className="team-booking-created">
                      {formatShortDateTime(b.createdAt, dateLocale)}
                    </td>
                  )}
                  <td>
                    {translateServiceLabel(t, b)}
                    {adminBrand === "all" && b.source && (
                      <div className="team-muted">{sourceLabel(t, b.source)}</div>
                    )}
                    {b.isFreeFirstMassagesessel && (
                      <span className="badge" style={{ marginLeft: "0.35rem" }}>
                        {t("bookings.badge.freeTawan")}
                      </span>
                    )}
                    {b.manual && (
                      <span className="badge" style={{ marginLeft: "0.35rem" }}>
                        {t("bookings.badge.manual")}
                      </span>
                    )}
                  </td>
                  <td>
                    <div>
                      {b.customer?.firstName} {b.customer?.lastName}
                    </div>
                    <div className="team-muted">{b.customer?.email}</div>
                    <div className="team-muted">{b.customer?.phone}</div>
                  </td>
                  <td>{bookingPriceLabel(b, (cents) => formatEuro(cents, locale), "—")}</td>
                  <td className="team-muted">{paymentLabel(b)}</td>
                  <td>
                    {isOnlinePaidBooking(b) ? (
                      <span className="badge">{statusLabel(t, displayedStatus)}</span>
                    ) : isReadOnlyStatus(b.status) ? (
                      <span className="badge team-badge-pending-email">
                        {statusLabel(t, b.status)}
                      </span>
                    ) : b.status === "cancelled" || b.status === "expired" ? (
                      <div className="team-cancelled-status">
                        <span className="team-muted">{statusLabel(t, b.status)}</span>
                        {b.status === "cancelled" &&
                        cancelledByLabel(t, b.cancelledBy, b.staffCancellationReason) ? (
                          <span className="team-muted team-cancelled-by">
                            {cancelledByLabel(t, b.cancelledBy, b.staffCancellationReason)}
                          </span>
                        ) : null}
                      </div>
                    ) : (
                      <select
                        value={
                          noShowConfirm?._id === b._id ? noShowConfirm.status : b.status
                        }
                        disabled={savingId === b._id}
                        onChange={(e) => handleStatusSelectChange(b, e.target.value)}
                        className="team-inline-select"
                      >
                        {STATUS_OPTIONS.map((val) => (
                          <option key={val} value={val}>
                            {statusLabel(t, val)}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td className="team-booking-customer-notes">
                    {customerNotes ? (
                      <span className="team-booking-customer-notes-text">
                        {customerNotes}
                      </span>
                    ) : (
                      <span className="team-muted">—</span>
                    )}
                  </td>
                  <td>
                    <textarea
                      className="team-notes-input"
                      defaultValue={internalNotesValue(b)}
                      rows={2}
                      placeholder={t("bookings.notesPlaceholder")}
                      disabled={b.status === "cancelled" || b.status === "expired"}
                      onBlur={(e) => {
                        if (e.target.value !== internalNotesValue(b)) {
                          handleNotesBlur(b._id, e.target.value);
                        }
                      }}
                    />
                  </td>
                  <td>
                    <div className="team-row-actions">
                      {b.status !== "cancelled" && b.status !== "expired" && (
                        <button
                          type="button"
                          className="team-delete-btn"
                          disabled={savingId === b._id}
                          onClick={() => openDeleteDialog(b, "cancel")}
                        >
                          {t("bookings.delete")}
                        </button>
                      )}
                      {isSuperAdmin && (
                        <button
                          type="button"
                          className="team-delete-btn team-delete-btn--permanent"
                          disabled={savingId === b._id}
                          onClick={() => openDeleteDialog(b, "permanent")}
                        >
                          {t("bookings.permanentDelete")}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      )}

      {pagination.pages > 1 && (
        <div className="team-pagination">
          <button
            type="button"
            disabled={pagination.page <= 1 || loading}
            onClick={() => load(pagination.page - 1)}
          >
            {t("common.back")}
          </button>
          <span>
            {t("bookings.pagination", {
              page: pagination.page,
              pages: pagination.pages,
              total: pagination.total,
            })}
          </span>
          <button
            type="button"
            disabled={pagination.page >= pagination.pages || loading}
            onClick={() => load(pagination.page + 1)}
          >
            {t("common.next")}
          </button>
        </div>
      )}

      <TeamDeleteBookingDialog
        open={Boolean(deleteTarget)}
        booking={deleteTarget}
        variant={deleteVariant}
        dateLocale={dateLocale}
        t={t}
        busy={Boolean(deleteTarget && savingId === deleteTarget._id)}
        onClose={() => !savingId && setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />

      <ConfirmDialog
        open={Boolean(noShowConfirm)}
        title={t("bookings.noShowTitle")}
        message={
          noShowConfirm
            ? t("bookings.noShowConfirm", {
                name: `${noShowConfirm.customer?.firstName || ""} ${noShowConfirm.customer?.lastName || ""}`.trim(),
                when: formatLong(noShowConfirm.start, dateLocale),
              })
            : ""
        }
        confirmLabel={t("bookings.noShowConfirmBtn")}
        cancelLabel={t("common.cancel")}
        busyLabel={t("bookings.noShowBusy")}
        busy={Boolean(noShowConfirm && savingId === noShowConfirm._id)}
        onConfirm={confirmNoShow}
        onCancel={() => !savingId && setNoShowConfirm(null)}
      />
    </div>
  );
};

export default BookingsTab;
